// LiveAnnounceSection.tsx — section « Annonce sur les réseaux » de l'onglet Configuration
//
// Annonce « Je suis en direct » (2026-10-17) : au passage à l'antenne (On air de la
// régie, ou de Kleon Studio en mode direct), admin-api publie une carte EN DIRECT sur
// la Page Facebook et le compte Instagram que le créateur a reliés dans sa fiche.
// Maquette validée par l'utilisateur : https://claude.ai/artifact/WQR5c16u4i7ZXV9KPGum3U
//
// Autonome, comme SubtitlesControl : la section lit et écrit son propre réglage
// (admin-api LiveAnnounceController), sans passer par le bouton Enregistrer de la
// fiche — un interrupteur ou une case s'applique aussitôt, le texte par son bouton.
// L'aperçu de la carte est l'image que le serveur publierait maintenant.
//
// Démo sans serveur : ?demoAnnonce (et ?demoAnnonce=echec, =nonrelie).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Toggle from '../../widget/inputs/Toggle';
import configStyles from './BroadcastingChannelConfig.module.scss';
import styles from './LiveAnnounceSection.module.scss';

import {
  fetchLiveAnnounce,
  updateLiveAnnounce,
  liveAnnounceCardUrl,
  LiveAnnounceError,
  LiveAnnounceNetwork,
  LiveAnnounceState,
  LiveAnnounceUpdate,
} from '../../../backendServices/broadcastingManagment/api_liveAnnounce';

interface Props {
  adminId   : number | undefined;
  channelId : number;
}

const NETWORK_LABELS: Record<LiveAnnounceNetwork, { name: string; detail: string }> = {
  facebook  : { name: 'Page Facebook', detail: 'post avec la carte et le lien cliquable' },
  instagram : { name: 'Instagram',     detail: 'post du fil, lien en légende (non cliquable)' },
};

const JOKERS = [ '{chaîne}', '{programme}', '{lien}' ];

// ── Démo (?demoAnnonce) ───────────────────────────────────────────────────────
const demoMode = (): string | null => {
  const value = new URLSearchParams(window.location.search).get('demoAnnonce');
  return value === null ? null : (value || 'ok');
};

const demoState = (variant: string): LiveAnnounceState => {
  const at = new Date(Date.now() - 3 * 60_000);
  return {
    channelId       : 0,
    enabled         : true,
    networks        : [ 'facebook', 'instagram' ],
    text            : null,
    defaultText     : "🔴 Je suis en direct sur Kleon.tv : {chaîne} — {programme}\nRejoignez-moi : {lien}",
    textMaxLength   : 500,
    link            : 'https://kleon.tv/le-grand-direct',
    preview         : "🔴 Je suis en direct sur Kleon.tv : Le Grand Direct — Débat du soir\nRejoignez-moi : https://kleon.tv/le-grand-direct",
    accounts        : {
      facebook  : { linked: true, label: 'Le Grand Direct' },
      instagram : variant === 'nonrelie' ? { linked: false, label: null } : { linked: true, label: 'legranddirect' },
    },
    announcedAt     : at.toISOString(),
    lastResult      : {
      at       : at.toISOString(),
      status   : 'done',
      networks : {
        facebook  : { ok: true, url: 'https://www.facebook.com/', error: null },
        instagram : variant === 'echec'
          ? { ok: false, url: null, error: "Error validating access token: Session has expired." }
          : { ok: true, url: 'https://www.instagram.com/', error: null },
      },
    },
  };
};

// ── Dates ───────────────────────────────────────────────────────────────────
const hourLabel = (date: Date) =>
  date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }).replace(':', ' h ');

const dayAndHour = (iso: string) => {
  const date  = new Date(iso);
  const today = new Date();
  const same  = date.toDateString() === today.toDateString();
  return same ? `Aujourd'hui à ${hourLabel(date)}`
              : `Le ${date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} à ${hourLabel(date)}`;
};

// ── Icônes ──────────────────────────────────────────────────────────────────
const Check = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#6EE7A0" strokeWidth="2.5" aria-hidden="true"><path d="M5 12l5 5L20 7"/></svg>
);
const Cross = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#F87171" strokeWidth="2.5" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
);

// ── Composant ───────────────────────────────────────────────────────────────
const LiveAnnounceSection: React.FC<Props> = ({ adminId, channelId }) => {
  const demo = useMemo(demoMode, []);

  const [state,       setState]       = useState<LiveAnnounceState | null>(demo ? demoState(demo) : null);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [busy,        setBusy]        = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [draft,       setDraft]       = useState('');
  const [cardVersion, setCardVersion] = useState(() => Date.now());
  const [cardFailed,  setCardFailed]  = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(async () => {
    if (demo || !adminId || !channelId) return;
    try {
      const fresh = await fetchLiveAnnounce(adminId, channelId);
      setState(fresh);
      setUnavailable(null);
    } catch (e) {
      // 404 : la chaîne n'est pas à ce compte (super-admin sur la chaîne d'un autre).
      setUnavailable(e instanceof LiveAnnounceError && e.status === 404
        ? "Réservé au créateur de la chaîne : l'annonce part sur ses propres comptes."
        : "Réglage indisponible pour le moment.");
    }
  }, [demo, adminId, channelId]);

  useEffect(() => { load(); }, [load]);

  // Le texte affiché suit le serveur tant que l'opérateur n'y touche pas.
  const savedText = state ? (state.text ?? state.defaultText) : '';
  useEffect(() => { setDraft(savedText); }, [savedText]);

  // Une annonce en cours d'envoi : relevé toutes les 5 s jusqu'à son résultat.
  const pending = state?.lastResult?.status === 'pending';
  useEffect(() => {
    if (!pending) return;
    const timer = window.setInterval(load, 5000);
    return () => window.clearInterval(timer);
  }, [pending, load]);

  const apply = async (change: LiveAnnounceUpdate) => {
    if (!state) return;
    setError(null);

    if (demo) {
      setState({ ...state, ...change, text: change.text !== undefined ? change.text : state.text } as LiveAnnounceState);
      return;
    }
    if (!adminId) return;

    setBusy(true);
    try {
      setState(await updateLiveAnnounce(adminId, channelId, change));
      setCardVersion(Date.now());
      setCardFailed(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "L'enregistrement a échoué.");
    } finally {
      setBusy(false);
    }
  };

  const toggleNetwork = (code: LiveAnnounceNetwork, on: boolean) => {
    if (!state) return;
    const next = on ? [ ...state.networks, code ] : state.networks.filter(n => n !== code);
    apply({ networks: ([ 'facebook', 'instagram' ] as LiveAnnounceNetwork[]).filter(n => next.includes(n)) });
  };

  const insertJoker = (joker: string) => {
    const area = textRef.current;
    const at   = area ? area.selectionStart : draft.length;
    const end  = area ? area.selectionEnd   : draft.length;
    const next = draft.slice(0, at) + joker + draft.slice(end);
    setDraft(next);
    requestAnimationFrame(() => {
      area?.focus();
      area?.setSelectionRange(at + joker.length, at + joker.length);
    });
  };

  if (unavailable) {
    return (
      <div className={configStyles.section}>
        <div className={configStyles.sectionTitle}>Annonce sur les réseaux</div>
        <div className={styles.muted}>{unavailable}</div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className={configStyles.section}>
        <div className={configStyles.sectionTitle}>Annonce sur les réseaux</div>
        <div className={styles.muted}>Chargement…</div>
      </div>
    );
  }

  const textChanged   = draft.trim() !== savedText.trim();
  const textTooLong   = draft.length > state.textMaxLength;
  const anyLinked     = state.accounts.facebook.linked || state.accounts.instagram.linked;
  const result        = state.lastResult;
  const cardSrc       = demo ? null : (adminId ? liveAnnounceCardUrl(adminId, channelId, cardVersion) : null);

  return (
    <div className={configStyles.section}>
      <div className={configStyles.sectionTitle}>Annonce sur les réseaux</div>

      <div className={styles.head}>
        <Toggle checked={state.enabled} form="switch" onChange={(on: boolean) => apply({ enabled: on })}/>
        <div className={styles.headText}>
          <span className={styles.headLabel}>Annoncer mes directs sur les réseaux</span>
          <span className={styles.muted}>
            Une publication part d'elle-même quand le direct passe à l'antenne (On air), depuis la régie
            comme depuis Kleon Studio, à chaque passage à l'antenne.
          </span>
        </div>
        {busy && <span className={styles.muted}>Enregistrement…</span>}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {state.enabled && !anyLinked && (
        <div className={styles.warning}>
          Aucun réseau n'est relié : reliez votre Page Facebook ou votre compte Instagram dans votre fiche
          créateur (Réseaux sociaux), sinon aucune annonce ne partira.
        </div>
      )}

      <div className={styles.grid}>
        <div className={styles.column}>
          <div className={styles.subTitle}>Publier sur</div>

          {(Object.keys(NETWORK_LABELS) as LiveAnnounceNetwork[]).map(code => {
            const account = state.accounts[code];
            const checked = account.linked && state.networks.includes(code);
            const id      = `annonce-${code}-${channelId}`;
            return (
              <div key={code} className={account.linked ? styles.network : styles.networkOff}>
                <input type="checkbox" id={id} checked={checked} disabled={!account.linked || busy}
                       onChange={e => toggleNetwork(code, e.target.checked)}/>
                <label htmlFor={id} className={styles.networkText}>
                  <span className={styles.networkName}>{NETWORK_LABELS[code].name}</span>
                  <span className={styles.muted}>
                    {account.linked
                      ? `${account.label ? (code === 'instagram' ? '@' + account.label : account.label) + ' · ' : ''}${NETWORK_LABELS[code].detail}`
                      : code === 'instagram' ? 'Aucun compte Instagram professionnel relié' : 'Aucune Page Facebook reliée'}
                  </span>
                </label>
                {account.linked
                  ? <span className={styles.linked}><Check/>{code === 'facebook' ? 'Reliée' : 'Relié'}</span>
                  : <span className={styles.muted}>Relier dans la fiche créateur</span>}
              </div>
            );
          })}

          <label htmlFor={`annonce-texte-${channelId}`} className={styles.subTitle}>Texte de l'annonce</label>
          <textarea id={`annonce-texte-${channelId}`} ref={textRef} className={styles.text} rows={4}
                    value={draft} onChange={e => setDraft(e.target.value)}/>
          <div className={styles.jokers}>
            <span>Insérer :</span>
            {JOKERS.map(j => (
              <button key={j} type="button" className={styles.joker} onClick={() => insertJoker(j)}>{j}</button>
            ))}
            <span className={styles.spacer}/>
            <span className={textTooLong ? styles.errorInline : undefined}>{draft.length} / {state.textMaxLength}</span>
          </div>
          <div className={styles.muted}>Sans programme en cours, « — {'{programme}'} » disparaît.</div>
          <div className={styles.actions}>
            <button type="button" className={styles.primary} disabled={!textChanged || textTooLong || busy}
                    onClick={() => apply({ text: draft })}>Enregistrer le texte</button>
            {state.text !== null && (
              <button type="button" className={styles.secondary} disabled={busy}
                      onClick={() => apply({ text: null })}>Revenir au texte par défaut</button>
            )}
          </div>
        </div>

        <div className={styles.column}>
          <div className={styles.subTitle}>Aperçu</div>
          <div className={styles.preview}>
            {cardSrc && !cardFailed
              ? <img className={styles.card} src={cardSrc} alt="Carte EN DIRECT de l'annonce" onError={() => setCardFailed(true)}/>
              : <div className={styles.cardMissing}>{demo ? 'Carte EN DIRECT (dessinée par le serveur)' : "Aperçu de la carte indisponible"}</div>}
            <div className={styles.previewText}>
              {state.preview}
              <span className={styles.muted}>La carte reprend le nom de la chaîne et le programme en cours au moment du On air.</span>
            </div>
          </div>

          <div className={styles.subTitle}>Dernière annonce</div>
          {!result && <div className={styles.muted}>Aucune annonce n'est encore partie pour cette chaîne.</div>}
          {result && (
            <div className={styles.result}>
              <div className={styles.networkName}>{dayAndHour(result.at)}</div>
              {result.status === 'pending' && <div className={styles.muted}>Envoi en cours…</div>}
              {result.status === 'done' && (Object.keys(result.networks) as LiveAnnounceNetwork[]).map(code => {
                const r = result.networks[code]!;
                return (
                  <div key={code} className={styles.resultLine}>
                    {r.ok ? <Check/> : <Cross/>}
                    <span>
                      {code === 'facebook' ? 'Facebook' : 'Instagram'} — {r.ok ? 'publiée' : `refusée : ${r.error ?? 'raison inconnue'}`}
                      {r.ok && r.url && <> · <a href={r.url} target="_blank" rel="noreferrer">Voir la publication</a></>}
                    </span>
                  </div>
                );
              })}
              {result.status === 'done' && Object.values(result.networks).some(r => r && !r.ok) && (
                <div className={styles.muted}>Le direct n'est pas touché : un échec chez Meta ne coupe jamais l'antenne.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveAnnounceSection;
